'use client';
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CURRENCIES, formatWithCurrency, formatUnitPrice, type CurrencyCode } from './appStore';
import { useAuth } from '@/contexts/AuthContext';

interface CurrencyContextValue {
  currency: CurrencyCode;
  setCurrency: (code: CurrencyCode) => void;
  format: (value: number) => string;
  formatUnit: (value: number) => string;
  currencySymbol: string;
  currencies: typeof CURRENCIES;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  currency: 'USD',
  setCurrency: () => {},
  format: (v) => `$${v.toFixed(2)}`,
  formatUnit: (v) => `$${v.toFixed(6)}`,
  currencySymbol: '$',
  currencies: CURRENCIES,
});

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const { company } = useAuth();
  const [currency, setCurrencyState] = useState<CurrencyCode>('USD');
  const [mounted, setMounted] = useState(false);

  // Sync currency from company record (owner-controlled, shared across all users)
  useEffect(() => {
    if (company?.currency && CURRENCIES.some((c) => c.code === company.currency)) {
      setCurrencyState(company.currency as CurrencyCode);
    }
    setMounted(true);
  }, [company?.currency]);

  const setCurrency = useCallback((code: CurrencyCode) => {
    setCurrencyState(code);
  }, []);

  const format = useCallback((value: number) => formatWithCurrency(value, currency), [currency]);
  const formatUnit = useCallback((value: number) => formatUnitPrice(value, currency), [currency]);

  const currencySymbol = CURRENCIES.find((c) => c.code === currency)?.symbol ?? '$';

  // Render with default until mounted to avoid hydration mismatch
  if (!mounted) {
    return (
      <CurrencyContext.Provider value={{ currency: 'USD', setCurrency, format: (v) => `$${v.toFixed(2)}`, formatUnit: (v) => `$${v.toFixed(6)}`, currencySymbol: '$', currencies: CURRENCIES }}>
        {children}
      </CurrencyContext.Provider>
    );
  }

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, format, formatUnit, currencySymbol, currencies: CURRENCIES }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
